// Prepara o dataset vindo de POST /planning/dataset para o motor do simulador.
//
// Feito UMA vez por carga: indexa a estrutura do DRE da empresa (nós SyncDRE + categorias
// SyncDRECat), agrega o realizado por categoria x mês e monta a BASE do ano do plano.
// Depois disso toda interação recalcula só em memória (ver simulate.js).
//
// A árvore segue exatamente a regra da tela DRE (dreCore.generateDreDataFromConfig):
// - nó = soma das categorias vinculadas a ele + de todos os descendentes (codDRESup → codDRE);
// - só entram categorias vinculadas no SyncDRECat (o resto do realizado fica fora da DRE);
// - raízes ordenadas por position; filhos ordenados por codDRE (localeCompare pt-BR, numérico).
//
// As categorias do ERP SEM vínculo com o DRE formam a seção "Categorias fora da DRE", abaixo da
// estrutura, para também serem simuladas e orçadas. Ela tem nós próprios (a seção e as categorias
// superiores do ERP) que ficam FORA das raízes do DRE: não entram nos totalizadores, nos indicadores,
// no ranking, no ajuste em massa nem nas metas em lote.

import {
  MONTH_INDEXES,
  monthOrdinal,
  fromOrdinal,
  parseMonthKey,
  shortMonthLabel,
  zeros12,
} from './periods';
import { categoryItemComparator, collectErpLeaves, compareCode } from './erpCategories';

export const INDICATOR_KEYS = ['RB', 'RL', 'LB', 'EBITDA', 'LL', 'IMPOSTOS'];
export const FINANCIAL_GROUP_KEYS = ['CF', 'CV', 'DF', 'DV', 'INV'];

// Quantos meses fechados entram na média da previsão (spec: últimos 3 realizados).
export const FORECAST_WINDOW = 3;

const toSet = (list) => new Set((Array.isArray(list) ? list : []).map((c) => String(c).trim()).filter(Boolean));

/**
 * Origem de cada mês do plano: o valor do DRE do mês equivalente mais recente do período de referência
 * — inclusive o mês corrente e os futuros, que valem como o DRE os mostra (`open: true`: o mês ainda não
 * fechou, então o valor pode estar parcial) — ou, sem mês equivalente na referência, a previsão (média
 * dos últimos meses fechados dela).
 */
export const buildMonthSources = ({ refStart: refStartKey, refEnd: refEndKey, lastClosedMonth } = {}) => {
  const refStart = parseMonthKey(refStartKey);
  const refEnd = parseMonthKey(refEndKey);
  const lastClosed = parseMonthKey(lastClosedMonth);

  // Todos os meses do período de referência, do mais antigo ao mais recente (como o DRE mostra).
  const refOrdinals = [];
  if (refStart && refEnd) {
    for (let o = monthOrdinal(refStart); o <= monthOrdinal(refEnd); o += 1) refOrdinals.push(o);
  }
  // Meses fechados: a janela da média de previsão dos meses sem equivalente na referência.
  const lastClosedOrdinal = lastClosed ? monthOrdinal(lastClosed) : null;
  const closedOrdinals = lastClosedOrdinal == null ? [] : refOrdinals.filter((o) => o <= lastClosedOrdinal);
  const forecastOrdinals = closedOrdinals.slice(-FORECAST_WINDOW);

  return MONTH_INDEXES.map((m) => {
    const sameMonth = refOrdinals.filter((o) => fromOrdinal(o).month === m + 1);
    if (sameMonth.length) {
      const ordinal = sameMonth[sameMonth.length - 1];
      return {
        type: 'realizado',
        ordinals: [ordinal],
        label: shortMonthLabel(fromOrdinal(ordinal)),
        open: lastClosedOrdinal == null || ordinal > lastClosedOrdinal,
      };
    }
    if (forecastOrdinals.length) {
      const first = fromOrdinal(forecastOrdinals[0]);
      const last = fromOrdinal(forecastOrdinals[forecastOrdinals.length - 1]);
      const label = forecastOrdinals.length > 1
        ? `média ${shortMonthLabel(first)}–${shortMonthLabel(last)}`
        : `média ${shortMonthLabel(last)}`;
      return { type: 'previsao', ordinals: forecastOrdinals, label };
    }
    return { type: 'sem_dados', ordinals: [], label: 'sem dados' };
  });
};

/** Realizado por categoria (Map cod -> Map(ordinal do mês -> valor)), só das categorias informadas. */
export const realizedByCategory = (values, catSet) => {
  const realized = new Map();
  (Array.isArray(values) ? values : []).forEach((v) => {
    const cod = String(v?.codCategoria ?? '').trim();
    if (!catSet.has(cod)) return;
    const ordinal = monthOrdinal({ year: Number(v.ano), month: Number(v.mes) });
    if (!Number.isFinite(ordinal)) return;
    if (!realized.has(cod)) realized.set(cod, new Map());
    const byMonth = realized.get(cod);
    byMonth.set(ordinal, (byMonth.get(ordinal) || 0) + (Number(v.valor) || 0));
  });
  return realized;
};

/**
 * Base do ano do plano por categoria (Map cod -> number[12]): em cada mês, o valor do DRE do mês
 * equivalente ou a média da previsão, conforme `monthSources`.
 */
export const buildBaseSeries = (realized, catList, monthSources) => {
  const base = new Map();
  catList.forEach((cod) => {
    const byMonth = realized.get(cod);
    const series = zeros12();
    if (byMonth) {
      monthSources.forEach((src, m) => {
        if (!src.ordinals.length) return;
        const total = src.ordinals.reduce((acc, o) => acc + (byMonth.get(o) || 0), 0);
        series[m] = src.type === 'previsao' ? total / src.ordinals.length : total;
      });
    }
    base.set(cod, series);
  });
  return base;
};

/** Nó raiz da seção "Categorias fora da DRE". */
export const OUTSIDE_DRE_NODE_ID = '__FORA_DRE__';
const outsideGroupNodeId = (cod) => `F|${cod}`;

const syntheticNode = (id, codDRE, descricao, parentId, depth) => ({
  id, codDRE, codDRESup: null, descricao, position: 0,
  isTotalizador: false, isConta: true, isSubConta: false, isCusto: false, totalizadorAV: false,
  parentId, childIds: [], directCats: [], allCats: [], depth,
});

/**
 * Seção "Categorias fora da DRE": as categorias do ERP (mesma regra do cenário por departamento) que
 * não estão vinculadas a nenhum nó do DRE, agrupadas pela categoria superior do ERP. Grava os nós em
 * `nodeById` e as categorias em `categories`; devolve { rootId, nodeIds, catList, revenueCats }.
 */
const buildOutsideSection = (data, dreCatSet, nodeById, categories) => {
  const { byCod, leaves } = collectErpLeaves(data);
  const cods = [...leaves.keys()].filter((cod) => !dreCatSet.has(cod));
  if (!cods.length) return { rootId: null, nodeIds: [], catList: [], revenueCats: new Set() };

  // Fora do cadastro do ERP, a descrição de um vínculo antigo do DRE (a nó inativo) é melhor que o código.
  const linkDescricao = new Map((Array.isArray(data.categories) ? data.categories : [])
    .map((link) => [String(link?.codCategoria ?? '').trim(), link?.descricao])
    .filter(([cod, descricao]) => cod && descricao));

  const root = syntheticNode(OUTSIDE_DRE_NODE_ID, OUTSIDE_DRE_NODE_ID, 'Categorias fora da DRE', null, 0);
  nodeById.set(root.id, root);
  cods.forEach((cod) => {
    const leaf = leaves.get(cod);
    let parent = root;
    if (leaf.groupCod) {
      const id = outsideGroupNodeId(leaf.groupCod);
      if (!nodeById.has(id)) {
        nodeById.set(id, syntheticNode(id, leaf.groupCod, byCod.get(leaf.groupCod)?.descricao || leaf.groupCod, root.id, 1));
        root.childIds.push(id);
      }
      parent = nodeById.get(id);
    }
    parent.directCats.push(cod);
    if (!byCod.has(cod) && linkDescricao.has(cod)) leaf.descricao = linkDescricao.get(cod);
    categories.set(cod, { cod, descricao: leaf.descricao, nodeIds: [parent.id] });
  });

  // Ordem do plano de contas (código estruturado) ou pelo nome; "lançamentos diretos" primeiro.
  const compareItems = categoryItemComparator([...byCod.keys(), ...cods]);
  const catItem = (cod) => ({ cod, label: leaves.get(cod).descricao });
  const sortCats = (node) => node.directCats.sort((a, b) => {
    const da = leaves.get(a).direct;
    const db = leaves.get(b).direct;
    if (da !== db) return da ? -1 : 1;
    return compareItems(catItem(a), catItem(b));
  });
  root.childIds.sort((a, b) => {
    const na = nodeById.get(a);
    const nb = nodeById.get(b);
    return compareItems({ cod: na.codDRE, label: na.descricao }, { cod: nb.codDRE, label: nb.descricao });
  });
  root.childIds.forEach((id) => {
    const group = nodeById.get(id);
    sortCats(group);
    group.allCats = [...group.directCats];
  });
  sortCats(root);
  root.allCats = [...root.childIds.flatMap((id) => nodeById.get(id).allCats), ...root.directCats];

  return {
    rootId: root.id,
    nodeIds: [root.id, ...root.childIds],
    catList: [...cods].sort(compareCode),
    // Receita pela natureza do cadastro do ERP: define o sinal do valor lançado em categoria sem base.
    revenueCats: new Set(cods.filter((cod) => leaves.get(cod).nature === 'R')),
  };
};

/**
 * @param {object} raw resposta de /planning/dataset
 * @param {object} options { planYear }
 *   Quando informada, substitui a base calculada a partir do realizado atual.
 */
export function prepareDataset(raw, { planYear } = {}) {
  const data = raw || {};
  const nodesRaw = (Array.isArray(data.nodes) ? data.nodes : [])
    .filter((n) => n && n.id && n.codDRE != null)
    .map((n) => ({ ...n, codDRE: String(n.codDRE), codDRESup: n.codDRESup ? String(n.codDRESup) : null }));

  const byCode = new Map(nodesRaw.map((n) => [n.codDRE, n]));
  const nodeById = new Map();

  nodesRaw.forEach((n) => {
    nodeById.set(n.id, {
      id: n.id,
      codDRE: n.codDRE,
      codDRESup: n.codDRESup,
      descricao: n.descricao || n.codDRE,
      position: Number(n.position) || 0,
      isTotalizador: Boolean(n.isTotalizador),
      isConta: Boolean(n.isConta),
      isSubConta: Boolean(n.isSubConta),
      isCusto: Boolean(n.isCusto),
      totalizadorAV: Boolean(n.totalizadorAV),
      parentId: null,
      childIds: [],
      directCats: [],
      allCats: [],
      depth: 0,
    });
  });

  // Filhos: por codDRESup apontando para um código existente (igual à DRE).
  nodesRaw.forEach((n) => {
    if (n.codDRESup && byCode.has(n.codDRESup)) {
      const parent = byCode.get(n.codDRESup);
      nodeById.get(n.id).parentId = parent.id;
      nodeById.get(parent.id).childIds.push(n.id);
    }
  });
  nodeById.forEach((node) => {
    node.childIds.sort((a, b) => compareCode(nodeById.get(a).codDRE, nodeById.get(b).codDRE));
  });

  // Raízes: sem codDRESup. Nós cujo pai não existe ficam fora da árvore (a DRE também os descarta).
  const rootIds = [...nodeById.values()]
    .filter((n) => !n.codDRESup)
    .sort((a, b) => (a.position - b.position) || compareCode(a.codDRE, b.codDRE))
    .map((n) => n.id);

  // Categorias vinculadas.
  const categories = new Map(); // cod -> { cod, descricao, nodeIds: [] }
  (Array.isArray(data.categories) ? data.categories : []).forEach((link) => {
    const cod = String(link?.codCategoria ?? '').trim();
    const nodeId = link?.syncDREId;
    if (!cod || !nodeById.has(nodeId)) return;
    if (!categories.has(cod)) categories.set(cod, { cod, descricao: link.descricao || cod, nodeIds: [] });
    const cat = categories.get(cod);
    if (!cat.nodeIds.includes(nodeId)) cat.nodeIds.push(nodeId);
    if (!cat.descricao && link.descricao) cat.descricao = link.descricao;
    const node = nodeById.get(nodeId);
    if (!node.directCats.includes(cod)) node.directCats.push(cod);
  });
  nodeById.forEach((node) => node.directCats.sort(compareCode));

  // allCats (subárvore) + profundidade.
  const collect = (id, depth) => {
    const node = nodeById.get(id);
    node.depth = depth;
    const set = new Set(node.directCats);
    node.childIds.forEach((childId) => collect(childId, depth + 1).forEach((c) => set.add(c)));
    node.allCats = [...set];
    return node.allCats;
  };
  rootIds.forEach((id) => collect(id, 0));

  // Nós dentro da árvore (alcançáveis pelas raízes), em pré-ordem.
  const orderedNodeIds = [];
  const walk = (id) => {
    orderedNodeIds.push(id);
    nodeById.get(id).childIds.forEach(walk);
  };
  rootIds.forEach(walk);
  const reachable = new Set(orderedNodeIds);

  // Categorias que de fato aparecem na DRE (vinculadas a nó alcançável).
  const catList = [...categories.values()]
    .filter((c) => c.nodeIds.some((id) => reachable.has(id)))
    .map((c) => c.cod)
    .sort(compareCode);
  const catSet = new Set(catList);

  // ---------------- Categorias fora da DRE ----------------
  const outside = buildOutsideSection(data, catSet, nodeById, categories);
  const allCatList = [...catList, ...outside.catList];

  // ---------------- Base do ano do plano ----------------
  const refEnd = parseMonthKey(data.refEnd);
  const realized = realizedByCategory(data.values, new Set(allCatList));
  const monthSources = buildMonthSources(data);
  const base = buildBaseSeries(realized, allCatList, monthSources);

  // ---------------- Indicadores e grupos financeiros ----------------
  const indicatorCats = {};
  INDICATOR_KEYS.forEach((key) => {
    indicatorCats[key] = new Set([...toSet(data.indicators?.[key])].filter((c) => catSet.has(c)));
  });
  const groupCats = {};
  FINANCIAL_GROUP_KEYS.forEach((key) => {
    groupCats[key] = new Set([...toSet(data.financialGroups?.[key])].filter((c) => catSet.has(c)));
  });

  // Receita Bruta usada pelo chip "Acompanha a receita": indicador RB configurado; sem config,
  // as categorias da primeira raiz do DRE (no modelo padrão, "Receita Bruta").
  let revenueCats = indicatorCats.RB;
  if (!revenueCats.size && rootIds.length) revenueCats = new Set(nodeById.get(rootIds[0]).allCats);

  // Padrão do chip: impostos sobre receita (indicador Impostos) e custos (nós marcados IsCusto).
  const defaultLinkedCats = new Set(indicatorCats.IMPOSTOS);
  nodeById.forEach((node) => {
    if (node.isCusto && reachable.has(node.id)) node.allCats.forEach((c) => defaultLinkedCats.add(c));
  });
  revenueCats.forEach((c) => defaultLinkedCats.delete(c));

  return {
    sync: data.sync || null,
    regime: data.regime || 'emissao',
    refStart: data.refStart,
    refEnd: data.refEnd,
    lastClosedMonth: data.lastClosedMonth || null,
    datasetVersion: data.datasetVersion || '',
    hasConfig: Boolean(data.hasConfig) && catList.length > 0,
    planYear: Number(planYear) || (refEnd ? refEnd.year + 1 : new Date().getFullYear() + 1),
    nodeById,
    rootIds,
    orderedNodeIds,
    categories,
    // Todas as categorias simuladas (DRE + fora da DRE): a projeção e o orçamento salvo cobrem as duas.
    catList: allCatList,
    base,
    monthSources,
    indicatorCats,
    groupCats,
    revenueCats,
    defaultLinkedCats,
    outsideRootId: outside.rootId,
    outsideNodeIds: outside.nodeIds,
    outsideCatSet: new Set(outside.catList),
    outsideRevenueCats: outside.revenueCats,
  };
}

/** Primeiro nó (na ordem da árvore) a que a categoria pertence — define herança de ajuste. */
export const primaryNodeOfCategory = (model, cod) => {
  const cat = model.categories.get(cod);
  if (!cat) return null;
  return cat.nodeIds.find((id) => model.nodeById.has(id)) || null;
};

/** Cadeia de nós do nó informado até a raiz (inclusive). */
export const ancestorChain = (model, nodeId) => {
  const chain = [];
  let current = nodeId;
  const guard = new Set();
  while (current && model.nodeById.has(current) && !guard.has(current)) {
    guard.add(current);
    chain.push(current);
    current = model.nodeById.get(current).parentId;
  }
  return chain;
};
